import type { ManagedReceivingPartnerApi as Api } from "@capstone/mock-api";
import { HelpAssistTicketInfoTicketOrderDto } from "mock-api/src/shared-v2-types";
import { ManagedType } from "src/app/core/constants";
import { Day } from "src/app/core/day.model";
import { Site } from "src/app/core/sites";
import { PointOfOrigin } from "src/app/partner/purchase-orders/base-purchase-order.model";
import { DoorGroup } from "src/app/partner/settings/door-groups/door-group.model";
import {
  deserializePurchaseOrderVendor,
  PurchaseOrderVendor,
  Vendor,
} from "src/app/partner/vendors/vendor.model";
import { getApiDetailsDecorator, getEnumMember } from "src/utils";
import { BaseHelpAssistTicketAppointmentOrder } from "./base-help-assist-ticket-appointment-order.model";

const api = getApiDetailsDecorator<Api.HelpAssistTicketAppointmentOrder>();

interface DeserializeArgs {
  site: Site;
}

export class HelpAssistTicketAppointmentOrder extends BaseHelpAssistTicketAppointmentOrder {
  private constructor(args: ClassProperties<HelpAssistTicketAppointmentOrder>) {
    super(args);
    this.doorGroup = args.doorGroup;
    this.id = args.id;
    this.site = args.site;
    this.vendor = args.vendor;
  }

  @api({
    key: "doorGroupID",
    navigationProperty: "doorGroup",
    uiModel: DoorGroup,
  })
  public override readonly doorGroup: Pick<DoorGroup, "id" | "name"> | null;
  @api() public readonly id: number;
  public readonly site: Site;
  @api({
    key: "vendorId",
    navigationProperty: "vendor",
    uiModel: Vendor,
  })
  public override readonly vendor: PurchaseOrderVendor;

  public static deserialize(
    apiModel: HelpAssistTicketInfoTicketOrderDto,
    { site }: DeserializeArgs,
  ): HelpAssistTicketAppointmentOrder {
    return new HelpAssistTicketAppointmentOrder({
      asnBillOfLadingNumber: apiModel.asnBolNumber ?? null,
      asnProNumber: apiModel.asnProNumber ?? null,
      backhaulPickupConfirmationNumber:
        apiModel.backhaulPickupConfirmationNumber ?? null,
      billOfLadingNumber: apiModel.bolNumber ?? null,
      buyerCode: apiModel.buyerCode ?? null,
      caseCount: apiModel.caseCount,
      comments: apiModel.comments ?? null,
      consigneeCode: apiModel.consigneeCode ?? null,
      doorGroup: apiModel.doorGroup ?? null,
      dueDate: apiModel.dueDate
        ? Day.deserialize(apiModel.dueDate, site.timeZone)
        : null,
      entryDate: apiModel.entryDate
        ? Day.deserialize(apiModel.entryDate, site.timeZone)
        : null,
      freightBasis: apiModel.freightBasis,
      id: apiModel.id,
      inboundPalletCount: apiModel.estimatedReceivedPalletCount,
      loadWeight: apiModel.loadWeight ?? null,
      managedType: getEnumMember(
        ManagedType,
        apiModel.managedType,
        ManagedType.TruckLoad,
      ),
      number: apiModel.number,
      orderId: apiModel.orderId ?? null,
      pickupDate: apiModel.pickupDate
        ? Day.deserialize(apiModel.pickupDate, site.timeZone)
        : null,
      pointOfOrigin: PointOfOrigin.deserialize(apiModel) ?? null,
      proNumber: apiModel.proNumber ?? null,
      site,
      sourceOrderId: apiModel.orderId ?? null,
      vendor: deserializePurchaseOrderVendor(apiModel.vendor, { site }),
      warehousePalletCount: apiModel.palletCount,
    });
  }

  public static deserializeList(
    value: readonly HelpAssistTicketInfoTicketOrderDto[],
    args: DeserializeArgs,
  ): HelpAssistTicketAppointmentOrder[] {
    return value.map((o) =>
      HelpAssistTicketAppointmentOrder.deserialize(o, args),
    );
  }
}
